// Leaderboard Page - Team performance rankings visible to all platform users
import React, { useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminHeader from '../../../shared/components/AdminHeader';

interface LeaderboardProps {
  brand: BrandConfig;
  Icons: IconsType;
  onNavigate: (module: string, subSection?: string) => void;
}

// Mock leaderboard data (in production, this comes from API)
const mockLeaderboardData = {
  period: 'week',
  periodLabel: 'This Week',
  rankings: [
    {
      rank: 1,
      operatorId: '660e8400-e29b-41d4-a716-446655440003',
      name: 'Admin User',
      avatar: 'A',
      email: 'admin@test.com',
      status: 'available',
      itemsCompleted: 55,
      avgCompletionTime: 6480,
      avgCompletionTimeFormatted: '1.8h',
      streak: 12,
      complianceRate: 98.2,
    },
    {
      rank: 2,
      operatorId: '660e8400-e29b-41d4-a716-446655440001',
      name: 'Operator One',
      avatar: 'O',
      email: 'operator1@test.com',
      status: 'available',
      itemsCompleted: 42,
      avgCompletionTime: 9000,
      avgCompletionTimeFormatted: '2.5h',
      streak: 8,
      complianceRate: 93.3,
    },
    {
      rank: 3,
      operatorId: '660e8400-e29b-41d4-a716-446655440002',
      name: 'Operator Two',
      avatar: 'O',
      email: 'operator2@test.com',
      status: 'busy',
      itemsCompleted: 38,
      avgCompletionTime: 11520,
      avgCompletionTimeFormatted: '3.2h',
      streak: 5,
      complianceRate: 82.6,
    },
    {
      rank: 4,
      operatorId: '660e8400-e29b-41d4-a716-446655440004',
      name: 'Operator Three',
      avatar: 'O',
      email: 'operator3@test.com',
      status: 'away',
      itemsCompleted: 28,
      avgCompletionTime: 14400,
      avgCompletionTimeFormatted: '4.0h',
      streak: 3,
      complianceRate: 85.7,
    },
    {
      rank: 5,
      operatorId: '660e8400-e29b-41d4-a716-446655440005',
      name: 'Operator Four',
      avatar: 'O',
      email: 'operator4@test.com',
      status: 'offline',
      itemsCompleted: 15,
      avgCompletionTime: 18000,
      avgCompletionTimeFormatted: '5.0h',
      streak: 0,
      complianceRate: 73.3,
    },
  ],
  myStats: {
    rank: 2,
    operatorId: '660e8400-e29b-41d4-a716-446655440001',
    name: 'Operator One',
    itemsCompleted: 42,
    streak: 8,
    complianceRate: 93.3,
    trend: 'up',
  },
  totalCompleted: 178,
  teamAvgStreak: 5,
};

// Podium component for top 3 performers
const PodiumDisplay = ({ topPerformers, brand }) => {
  if (topPerformers.length < 3) return null;

  const positions = [
    { data: topPerformers[1], position: 2, height: 'h-28', bg: '#C0C0C0' }, // Silver - 2nd
    { data: topPerformers[0], position: 1, height: 'h-36', bg: '#FFD700' }, // Gold - 1st
    { data: topPerformers[2], position: 3, height: 'h-24', bg: '#CD7F32' }, // Bronze - 3rd
  ];

  return (
    <div className="flex items-end justify-center gap-4 mb-8">
      {positions.map(({ data, position, height, bg }) => (
        <div key={position} className="flex flex-col items-center">
          {/* Avatar */}
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shadow-lg mb-2"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            {data.avatar}
          </div>
          {/* Name */}
          <div className="text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
            {data.name}
          </div>
          {/* Stats */}
          <div className="text-xs mb-2" style={{ color: brand.colors.textLight }}>
            {data.itemsCompleted} completed
          </div>
          {/* Podium block */}
          <div
            className={`${height} w-24 rounded-t-lg flex flex-col items-center justify-start pt-3`}
            style={{ backgroundColor: bg }}
          >
            <span className="text-2xl font-bold text-white">{position}</span>
            {data.streak > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <span className="text-white text-xs">🔥 {data.streak}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Personal stats card
const PersonalStatsCard = ({ myStats, totalOperators, brand }) => {
  if (!myStats) return null;

  const trendIcons = {
    up: '📈',
    down: '📉',
    same: '➖',
  };

  const percentile = Math.round(((totalOperators - myStats.rank) / totalOperators) * 100);

  return (
    <div
      className="rounded-xl p-5 mb-6"
      style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold" style={{ color: brand.colors.secondary }}>
          Your Performance
        </h3>
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {trendIcons[myStats.trend]} {myStats.trend === 'up' ? 'Trending up' : myStats.trend === 'down' ? 'Trending down' : 'Holding steady'}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="text-center">
          <div className="text-3xl font-bold" style={{ color: brand.colors.link }}>
            #{myStats.rank}
          </div>
          <div className="text-sm" style={{ color: brand.colors.textLight }}>
            Rank
          </div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold" style={{ color: brand.colors.text }}>
            {myStats.itemsCompleted}
          </div>
          <div className="text-sm" style={{ color: brand.colors.textLight }}>
            Completed
          </div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold" style={{ color: myStats.streak > 0 ? '#F59E0B' : brand.colors.textLight }}>
            {myStats.streak > 0 ? `🔥 ${myStats.streak}` : '0'}
          </div>
          <div className="text-sm" style={{ color: brand.colors.textLight }}>
            Day Streak
          </div>
        </div>
        <div className="text-center">
          <div
            className="text-3xl font-bold"
            style={{ color: myStats.complianceRate >= 95 ? '#10B981' : myStats.complianceRate >= 85 ? '#F59E0B' : '#EF4444' }}
          >
            {myStats.complianceRate}%
          </div>
          <div className="text-sm" style={{ color: brand.colors.textLight }}>
            SLA Compliance
          </div>
        </div>
      </div>

      {percentile > 0 && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: brand.colors.border }}>
          <div className="text-sm text-center" style={{ color: brand.colors.textLight }}>
            You're in the top <strong style={{ color: brand.colors.link }}>{100 - percentile}%</strong> of performers this week
          </div>
        </div>
      )}
    </div>
  );
};

// Streak indicator component
const StreakIndicator = ({ streak, brand }) => {
  if (streak === 0) return <span style={{ color: brand.colors.textLight }}>-</span>;

  return (
    <span className="flex items-center gap-1">
      <span>🔥</span>
      <span style={{ color: streak >= 7 ? '#F59E0B' : brand.colors.text }}>{streak} days</span>
    </span>
  );
};

const Leaderboard: React.FC<LeaderboardProps> = ({ brand, Icons, onNavigate: _onNavigate }) => {
  const [period, setPeriod] = useState('week');
  const [leaderboardData] = useState(mockLeaderboardData);

  const statusColors = {
    available: '#10B981',
    busy: '#F59E0B',
    away: '#6B7280',
    offline: '#EF4444',
  };

  const getComplianceColor = (rate) => {
    if (rate >= 95) return '#10B981';
    if (rate >= 85) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
      <AdminHeader
        title="Team Leaderboard"
        subtitle="Performance rankings across the team"
        brand={brand}
        Icons={Icons}
        rightContent={
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          >
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        }
      />

      <div className="flex-1 overflow-auto p-6">
        {/* Team Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <div
            className="rounded-xl p-4 text-center"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <div className="text-3xl font-bold" style={{ color: brand.colors.link }}>
              {leaderboardData.totalCompleted}
            </div>
            <div className="text-sm" style={{ color: brand.colors.textLight }}>
              Total Completed
            </div>
          </div>
          <div
            className="rounded-xl p-4 text-center"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <div className="text-3xl font-bold" style={{ color: brand.colors.text }}>
              {leaderboardData.rankings.length}
            </div>
            <div className="text-sm" style={{ color: brand.colors.textLight }}>
              Team Members
            </div>
          </div>
          <div
            className="rounded-xl p-4 text-center col-span-2 sm:col-span-1"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <div className="text-3xl font-bold" style={{ color: '#F59E0B' }}>
              🔥 {leaderboardData.teamAvgStreak}
            </div>
            <div className="text-sm" style={{ color: brand.colors.textLight }}>
              Avg Streak
            </div>
          </div>
        </div>

        {/* Podium - Top 3 */}
        <div
          className="rounded-xl p-6 mb-6"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <h3 className="font-semibold text-center mb-6" style={{ color: brand.colors.secondary }}>
            🏆 Top Performers - {leaderboardData.periodLabel}
          </h3>
          <PodiumDisplay topPerformers={leaderboardData.rankings.slice(0, 3)} brand={brand} />
        </div>

        {/* Personal Stats */}
        <PersonalStatsCard
          myStats={leaderboardData.myStats}
          totalOperators={leaderboardData.rankings.length}
          brand={brand}
        />

        {/* Full Rankings Table */}
        <div
          className="rounded-xl p-5"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <h3 className="font-semibold mb-4" style={{ color: brand.colors.secondary }}>
            Full Rankings
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Rank</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Team Member</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Status</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Completed</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Avg Time</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Streak</th>
                  <th className="text-left py-2 px-3 text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Compliance</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.rankings.map((item, index) => (
                  <tr
                    key={item.operatorId}
                    style={{
                      borderBottom: index < leaderboardData.rankings.length - 1 ? `1px solid ${brand.colors.border}` : 'none',
                      backgroundColor: item.operatorId === leaderboardData.myStats?.operatorId ? `${brand.colors.primary}10` : 'transparent',
                    }}
                  >
                    <td className="py-3 px-3">
                      <span
                        className="font-bold text-lg"
                        style={{
                          color: item.rank === 1 ? '#FFD700' : item.rank === 2 ? '#C0C0C0' : item.rank === 3 ? '#CD7F32' : brand.colors.text
                        }}
                      >
                        {item.rank <= 3 ? ['🥇', '🥈', '🥉'][item.rank - 1] : `#${item.rank}`}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                          style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                        >
                          {item.avatar}
                        </div>
                        <span className="font-medium" style={{ color: brand.colors.text }}>
                          {item.name}
                          {item.operatorId === leaderboardData.myStats?.operatorId && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-sm" style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}>
                              You
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: statusColors[item.status] }}
                        />
                        <span className="text-sm capitalize" style={{ color: brand.colors.textLight }}>
                          {item.status}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <span className="font-medium" style={{ color: brand.colors.text }}>
                        {item.itemsCompleted}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-sm" style={{ color: brand.colors.textLight }}>
                        {item.avgCompletionTimeFormatted || '-'}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <StreakIndicator streak={item.streak} brand={brand} />
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className="font-medium"
                        style={{ color: getComplianceColor(item.complianceRate) }}
                      >
                        {item.complianceRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
